import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'

import { Text, Button, Link, Icon, Card } from '@/ui'
import { faCalculator } from '@fortawesome/pro-solid-svg-icons'
import { useFetchResponse } from '@/utils/sharedHooks'
import { fetchInsightsTableData } from '../../shared/financeActions'
import { INSIGHTS_TYPE } from '../../shared/utils'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { TextColors, TypographyStyles } from '@/styles'

export interface CategorySumTableProps {
  header: string
  tableData: { id: number; categoryName: string; amountInCents: number }[]
  sumColor: keyof typeof TextColors
}

export const CategorySumTable = ({
  header,
  tableData,
  sumColor = 'MOSS',
}: CategorySumTableProps) => {
  const trackMessage =
    header === 'Income'
      ? 'view-income-transactions'
      : 'view-expenses-transactions'

  return (
    <>
      <Text variant={TypographyStyles.EYEBROW} className="mb-4">
        {header}
      </Text>
      <div className="space-y-2">
        {tableData.map((category) => (
          <div
            key={category.id}
            className="flex justify-between items-center py-2"
          >
            <Text variant={TypographyStyles.BODY_SM}>
              {category.categoryName}
            </Text>
            <Text variant={TypographyStyles.BODY_SM} className={sumColor}>
              {formatCurrencyFromCents(category.amountInCents)}
            </Text>
          </div>
        ))}

        <div>
          <Link
            href="/transactions/business"
            trackMessage={trackMessage}
            trackAttributes={{ screen: 'practice insights' }}
            newPage={false}
          >
            View Transactions →
          </Link>
        </div>
      </div>
    </>
  )
}

export const ProfitLossInsights = ({
  startDate,
  endDate,
}: {
  startDate: string
  endDate: string
}) => {
  const navigate = useNavigate()
  const track = useAnalyticsTrack()

  const incomeTableResponse = useFetchResponse(
    fetchInsightsTableData,
    INSIGHTS_TYPE.INCOME,
    startDate,
    endDate
  )

  const expensesTableResponse = useFetchResponse(
    fetchInsightsTableData,
    INSIGHTS_TYPE.EXPENSES,
    startDate,
    endDate
  )

  const incomeTableData = useMemo(() => {
    if (!incomeTableResponse) return []
    return incomeTableResponse.slice(0, 5).map((row) => ({
      id: row.transaction_category_id,
      categoryName: row.transaction_category_name,
      amountInCents: row.transactions_sum_in_cents,
    }))
  }, [incomeTableResponse])

  const expensesTableData = useMemo(() => {
    if (!expensesTableResponse) return []
    return expensesTableResponse.slice(0, 5).map((row) => ({
      id: row.transaction_category_id,
      categoryName: row.transaction_category_name,
      amountInCents: row.transactions_sum_in_cents,
    }))
  }, [expensesTableResponse])

  const { inputStartDate, inputEndDate } = useMemo(() => {
    return {
      inputStartDate: DateTime.fromFormat(
        startDate,
        DATE_FORMATS_LUXON.YEAR_MONTH_DAY
      ).toFormat(DATE_FORMATS_LUXON.INPUT),
      inputEndDate: DateTime.fromFormat(
        endDate,
        DATE_FORMATS_LUXON.YEAR_MONTH_DAY
      ).toFormat(DATE_FORMATS_LUXON.INPUT),
    }
  }, [startDate, endDate])

  return (
    <Card>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex flex-row items-center gap-3">
          <Icon
            icon={faCalculator}
            backgroundStyle="rounded"
            backgroundColor="black"
            size="sm"
            color="text-neutral-0"
            backgroundClassName="hidden sm:flex"
          />
          <div>
            <Text variant={TypographyStyles.H1} className="mb-0">
              Profit & Loss
            </Text>
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              Summarizes your income and expenses over time
            </Text>
          </div>
        </div>
        <Button
          className="w-full sm:w-auto"
          variant="primary"
          onClick={() => {
            navigate(
              `/reports/profit-loss?startDate=${inputStartDate}&endDate=${inputEndDate}&from=insights`
            )
            track('view-full-statement', {
              screen: 'practice insights',
            })
          }}
        >
          View Full Statement
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-6 mt-6">
        <div className="w-1/2">
          <CategorySumTable
            header="INCOME"
            tableData={incomeTableData}
            sumColor="MOSS"
          />
        </div>

        <div className="w-1/2">
          <CategorySumTable
            header="EXPENSES"
            tableData={expensesTableData}
            sumColor="YELLOW"
          />
        </div>
      </div>
    </Card>
  )
}
